// © 2026 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {Component, ElementRef, HostListener, effect, input, output, signal, viewChild} from '@angular/core';

/** What has to be typed to arm the confirm button. Exported so the spec and the story type the real word. */
export const PROVIDER_SWITCH_CONFIRM_WORD = 'BESTÄTIGEN';

/**
 * Confirms switching the fiskal provider of one legal area (#613/#631) — the irreversible half of
 * **Owner → Rechtliches**.
 *
 * ### Why a dialog, and why it still asks for a typed word
 *
 * This used to be `window.prompt(…)`. A native prompt is a browser chrome box: it cannot say which area and
 * which provider it is about, it cannot be styled, it cannot be read by an automated a11y check, it blocks
 * the whole tab, and in a Storybook frame it hangs the story. What it *did* do was right, and is kept here:
 * the reader has to type the word, so a stray Enter or a mis-click on the wrong card can never commit a
 * provider switch. The sentence is the one the end-user documentation quotes verbatim
 * (`StaticPages/docs/.../verwaltung/recht/tse.mdx`), so it stays word for word.
 *
 * Dismissible like `TssReinitializeConfirmDialogComponent` and the revoke dialog (backdrop click and
 * `Escape` both cancel): nothing has changed yet, so cancelling is always the safe answer. Focus starts on
 * the **input**, not on „Abbrechen" — the typed word is what guards this dialog, so the safe-default-focus
 * rule is already satisfied by the confirm button being disabled, and the reader's next action is typing.
 */
@Component({
  selector: 'app-provider-switch-confirm-dialog',
  standalone: true,
  template: `
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="flex max-h-[100dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl
                  sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="provider-switch-heading"
           aria-describedby="provider-switch-body" (click)="$event.stopPropagation()">
        <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">

          <h2 id="provider-switch-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Anbieter für {{ areaName() }} auf {{ providerName() }} wechseln?
          </h2>

          <div id="provider-switch-body" class="space-y-3 text-sm text-gray-700">
            <p>Diese Aktion kann nicht rückgängig gemacht werden, zum Fortfahren bitte
              '{{ confirmWord }}' eingeben.</p>
            <p class="text-xs text-gray-500">
              Ein Wechsel ist nur möglich, solange dieser Standort keine registrierten Geräte und keinen
              offenen Kassenabschluss hat.
            </p>
          </div>

          <div>
            <label for="provider-switch-input"
                   class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Bestätigung
            </label>
            <input #confirmInput id="provider-switch-input" type="text" autocomplete="off"
                   [attr.placeholder]="confirmWord" [value]="typed()"
                   (input)="typed.set(read($event))" (keydown.enter)="confirm()"
                   class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5
                          text-gray-900 shadow-sm transition-all duration-200 placeholder-gray-300
                          focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end gap-3 border-t border-gray-100 p-4 sm:px-6">
          <button type="button" (click)="cancel()"
                  class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                         py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                         hover:border-gray-300 hover:bg-gray-50 active:scale-95">
            Abbrechen
          </button>
          <button type="button" (click)="confirm()" [disabled]="!armed() || busy()"
                  data-testid="provider-switch-confirm"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-700 px-5 py-2.5
                         text-sm font-semibold text-white shadow-lg transition-all duration-200
                         hover:bg-theme-primary-800 active:scale-95 disabled:cursor-not-allowed
                         disabled:opacity-30 disabled:grayscale">
            Ja, Anbieter wechseln
          </button>
        </div>
      </div>
    </div>
  `
})
export class ProviderSwitchConfirmDialogComponent {
  /**
   * „Cloud-TSE" / „Keiner" — the provider being switched TO, as a person reads it.
   *
   * Always passed from `LegalProviderHelper.GetName` and never written out here, so the dialog cannot drift
   * from the card the reader just clicked — and so #241's rule that no screen names the supplier holds for
   * this heading without a second string to police.
   */
  readonly providerName = input.required<string>();
  /** The legal area, e.g. „KassenSichV (TSE)", so the dialog names what it is about. */
  readonly areaName = input.required<string>();
  /** Disables the confirm button while the `SelectProvider` call is in flight. */
  readonly busy = input(false);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  protected readonly confirmWord = PROVIDER_SWITCH_CONFIRM_WORD;
  protected readonly typed = signal('');

  /** Trimmed, case-sensitive: the word is the guard, and a lowercase „bestätigen" is not it. */
  protected readonly armed = () => this.typed().trim() === PROVIDER_SWITCH_CONFIRM_WORD;

  private readonly confirmInput = viewChild<ElementRef<HTMLInputElement>>('confirmInput');

  constructor() {
    effect(() => {
      const input = this.confirmInput()?.nativeElement;
      if (input) input.focus();
    });
  }

  protected read(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  protected confirm(): void {
    if (!this.armed() || this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
